import React, { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FaUserCircle, FaCoins, FaWallet, FaChartLine,
  FaArrowRight, FaHistory, FaCrown, FaArrowLeft, FaStar,
  FaUserFriends, FaGem, FaShieldAlt,
  FaCopy
} from "react-icons/fa";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { useAppContext } from "../context/AppContext";
import InvestPlanCards from "./InvestPlanCards";
import Alert from "./Alert";
import ReferralSection from "./ReferralSection";
import Analytics from "./analytics";
import Wallet from "./wallet";

/* ── Theme ── */
const T = {
  bg: "#050d1a",
  bgCard: "#0a1628",
  bgCard2: "#0d1f38",
  border: "#1a2d4a",
  accent: "#eab308",
  accentDim: "rgba(234,179,8,0.1)",
  accentGlow: "rgba(234,179,8,0.3)",
  blue: "#3b82f6",
  blueDim: "rgba(59,130,246,0.1)",
  textPrimary: "#f0f6ff",
  textSecondary: "#94a3b8",
  textMuted: "#4b6280",
  success: "#22c55e",
  successDim: "rgba(34,197,94,0.1)",
  danger: "#ef4444",
};

const getPlanName = (dbName) => {
  const plans = {
    plan1: "Core Growth",
    plan2: "Institutional Blend",
    plan3: "Founder's Syndicate",
  };
  return plans[dbName] || dbName;
};

export default function UserDashboard({ me }) {
  const navigate = useNavigate();
  const {
    FetchUserData, HandleFetchUserData, showAlert, loading,
    PaymentINV, alert, fetchLatestInvoice, checkInvoiceStatus,
    fetchWithdrawHistory, handleRefreshStatus, withdrawHistory, invoice, timeLeft, setInvoice, setTimeLeft
  } = useAppContext();

  const location = useLocation();
  const [showPlans, setShowPlans] = useState(false);
  const [showReferral, setShowReferral] = useState(false);
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [showWallet, setShowWallet] = useState(false);
  const [searchParams] = useSearchParams();

  /* ── all original effects / logic preserved ── */
  useEffect(() => {
    const tab = searchParams.get("tab");
    setShowAnalytics(tab === "analytics");
    setShowWallet(tab === "wallet");
    if (tab === "wallet" || tab === "analytics") {
      setShowPlans(false);
      setShowReferral(false);
    }
  }, [searchParams]);

  useEffect(() => { fetchWithdrawHistory(); }, [fetchWithdrawHistory]);
  useEffect(() => { HandleFetchUserData(); }, [HandleFetchUserData]);
  useEffect(() => { fetchLatestInvoice(); }, []);
  useEffect(() => { setShowPlans(location.pathname === "/plans"); }, [location.pathname]);

  const stats = useMemo(() => {
    const activePlanArr = Array.isArray(FetchUserData?.activePlan) ? FetchUserData.activePlan : [];
    return {
      name: FetchUserData?.name || me?.email || "Investor",
      points: FetchUserData?.TotalPoints ?? 0,
      totalEarnings: FetchUserData?.totalEarnings ?? 0,
      todayTasks: FetchUserData?.todayTasksCompleted ?? 0,
      isActivePlan: !!FetchUserData?.isActivePlan,
      isActive: !!FetchUserData?.isActive,
      activePlan: activePlanArr,
      level: Number(FetchUserData?.level ?? 0),
      walletConnected: !!FetchUserData?.walletAddress,
      multiplierAtStart: Number(FetchUserData?.multiplierAtStart),
      NetTotalEarning: Number(FetchUserData?.NetTotalEarning || 0),
    };
  }, [FetchUserData, me]);

const userPlan = useMemo(() => {
  const plans = stats.activePlan || [];
  const latest = plans[plans.length - 1];
  const plan = latest?.plan;

  if (!plan) return "No plan select";

  if (plan.isClosed) {
    return "Reinvest Required";
  }

  return "$" + Number(plan.amount || 0);
}, [stats.activePlan]);


const latestPlanClosed = useMemo(() => {
  const plans = stats.activePlan || [];
  const latest = plans[plans.length - 1];

  return Boolean(latest?.plan?.isClosed);
}, [stats.activePlan]);




  const activePlanDisplayName = useMemo(() => {
    if (!stats.activePlan || stats.activePlan.length === 0) return "No Plan";
    const lastPlan = stats.activePlan[stats.activePlan.length - 1];
    return getPlanName(lastPlan?.plan?.name);
  }, [stats.activePlan]);

  const handleVerifyPayment = async () => {
    if (PaymentINV?.referenceId) {
      await checkInvoiceStatus(PaymentINV.referenceId);
    }
  };

  useEffect(() => {
    if (!invoice?.remainingMs) return;
    setTimeLeft(invoice.remainingMs);
    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1000) {
          clearInterval(timer);
          setInvoice(prevInv => ({ ...prevInv, status: "expired" }));
          return 0;
        }
        return prev - 1000;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [invoice]);

  const formatTime = ms => {
    const total = Math.floor(ms / 1000);
    const h = String(Math.floor(total / 3600)).padStart(2, "0");
    const m = String(Math.floor((total % 3600) / 60)).padStart(2, "0");
    const s = String(total % 60).padStart(2, "0");
    return `${h}:${m}:${s}`;
  };

  const planProgress = useMemo(() => {
    // if (!stats.isActivePlan || !stats.activePlan?.length) {
    //   return { percent: 0, remaining: 0, cap: 0, earned: 0 };
    // }
    const last = stats.activePlan[stats.activePlan.length - 1];
    const plan = last?.plan;
    const principal = Number(plan?.amount || 0);
    const multiplier = Number(stats?.multiplierAtStart);
    const capProfit = principal * multiplier;
    const earned = Number(stats?.NetTotalEarning || 0);
    const percent = capProfit > 0 ? Math.min(100, (earned / capProfit) * 100) : 0;
    const remaining = Math.max(0, capProfit - earned);
    return { percent, remaining, cap: capProfit, earned };
  }, [stats]);

  const copyAddress = () => {
    navigator.clipboard.writeText(invoice.address);
    showAlert("success", "Wallet address copied");
  };

  /* ── sub-page wrapper, dark themed ── */
  const renderSubPage = (component, onBack) => (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      style={{
        paddingTop: 96, paddingBottom: 40,
        paddingLeft: "clamp(16px, 4vw, 24px)", paddingRight: "clamp(16px, 4vw, 24px)",
        minHeight: "100vh", background: T.bg,
      }}
    >
      <button
        onClick={onBack}
        style={{
          marginBottom: 32,
          display: "inline-flex", alignItems: "center", gap: 8,
          padding: "12px 24px", borderRadius: 16,
          background: T.bgCard,
          border: `1px solid rgba(234,179,8,0.25)`,
          color: T.accent,
          fontWeight: 900, fontSize: 11, letterSpacing: "0.15em",
          textTransform: "uppercase", cursor: "pointer",
          boxShadow: "0 4px 20px rgba(0,0,0,0.3)",
          transition: "all 0.2s",
        }}
        onMouseEnter={e => { e.currentTarget.style.borderColor = T.accent; e.currentTarget.style.background = T.accentDim; }}
        onMouseLeave={e => { e.currentTarget.style.borderColor = "rgba(234,179,8,0.25)"; e.currentTarget.style.background = T.bgCard; }}
      >
        <FaArrowLeft /> Exit Terminal
      </button>
      <div style={{ maxWidth: 1280, margin: "0 auto" }}>{component}</div>
    </motion.div>
  );

  if (showPlans) return renderSubPage(<InvestPlanCards />, () => setShowPlans(false));
  if (showReferral) return renderSubPage(<ReferralSection code={FetchUserData?.referralCode || FetchUserData?._id} onBack={() => setShowReferral(false)} />);
  if (showAnalytics) return renderSubPage(<Analytics />, () => navigate("/"));
  if (showWallet) return renderSubPage(<Wallet />, () => navigate("/"));

  return (
    <div style={{
      minHeight: "100vh", color: T.textPrimary,
      paddingBottom: 80, paddingLeft: "clamp(12px, 3vw, 24px)", paddingRight: "clamp(12px, 3vw, 24px)",
      fontFamily: "system-ui,-apple-system,sans-serif",
    }}>
      <Alert
        className="mt-24"
        type={alert.type}
        message={alert.message}
        isOpen={alert.isOpen}
        onClose={() => showAlert({ isOpen: false, type: "", message: "" })}
      />

      <div style={{ maxWidth: 1280, margin: "0 auto", paddingTop: 112, display: "flex", flexDirection: "column", gap: 32 }}>

        {/* ══════════ HEADER CARD ══════════ */}
        <motion.div
          initial={{ opacity: 0, scale: 0.97, y: -20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          style={{
            position: "relative", overflow: "hidden",
            borderRadius: 36,
            background: T.bgCard,
            border: `1px solid ${T.border}`,
            boxShadow: "0 25px 80px rgba(0,0,0,0.55)",
          }}
        >
          {/* top accent line */}
          <div style={{
            position: "absolute", top: 0, left: 0, right: 0, height: 3, zIndex: 2,
            background: `linear-gradient(90deg,${T.accent},${T.blue},transparent)`,
          }} />

          {/* glow */}
          <div style={{
            position: "absolute", top: -160, right: -160,
            width: "clamp(300px, 50vw, 500px)", height: "clamp(300px, 50vw, 500px)", borderRadius: "50%",
            background: "radial-gradient(ellipse,rgba(234,179,8,0.08) 0%,transparent 70%)",
            pointerEvents: "none",
          }} />

          {/* ── PENDING INVOICE CARD ── */}
          <AnimatePresence>
            {invoice?.status === "pending" && timeLeft > 0 && (
              <motion.div
                initial={{ opacity: 0, y: -16 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                style={{ margin: "clamp(16px, 4vw, 24px) clamp(16px, 4vw, 32px) 0", position: "relative", zIndex: 1 }}
              >
                <div style={{
                  background: `linear-gradient(135deg,${T.bgCard2},#0a1830)`,
                  border: `1px solid rgba(59,130,246,0.3)`,
                  borderRadius: 28, padding: "clamp(16px, 4vw, 24px)",
                  boxShadow: "0 12px 36px rgba(0,0,0,0.4)",
                }}>
                  <div style={{
                    display: "flex", flexWrap: "wrap", justifyContent: "space-between",
                    alignItems: "flex-start", gap: 16, marginBottom: 20,
                  }}>
                    <div>
                      <p style={{ fontSize: 10, color: T.textMuted, textTransform: "uppercase", fontWeight: 900, letterSpacing: "0.15em", marginBottom: 6 }}>
                        Amount
                      </p>
                      <h2 style={{ fontSize: "clamp(1.5rem, 5vw, 1.8rem)", fontWeight: 900, color: T.blue, margin: 0 }}>
                        ${invoice.amountUSD +8}
                        <span style={{ fontSize: "1rem", marginLeft: 8, color: T.textMuted }}>USDT</span>
                      </h2>
                    </div>

                    <div style={{
                      background: "rgba(0,0,0,0.3)",
                      border: `1px solid ${T.border}`,
                      padding: "10px 16px", borderRadius: 14,
                    }}>
                      <p style={{ fontSize: 9, color: T.textMuted, fontWeight: 900, textTransform: "uppercase", marginBottom: 3 }}>
                        Expires In
                      </p>
                      <p style={{ color: T.danger, fontWeight: 900, fontSize: 13, margin: 0 }}>
                        {formatTime(timeLeft)}
                      </p>
                    </div>
                  </div>

                  {/* wallet address */}
                  <div style={{
                    background: "rgba(0,0,0,0.3)",
                    border: `1px solid ${T.border}`,
                    borderRadius: 16, padding: "12px 16px",
                    display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", gap: 10,
                  }}>
                    <div style={{ flex: "1 1 auto", minWidth: 0 }}>
                      <p style={{ fontSize: 9, textTransform: "uppercase", letterSpacing: "0.15em", color: T.textMuted, fontWeight: 900, marginBottom: 4 }}>
                        Deposit Address
                      </p>
                      <p style={{ fontFamily: "monospace", fontSize: "clamp(11px, 3vw, 13px)", fontWeight: 700, color: T.textPrimary, margin: 0, wordBreak: "break-all" }}>
                        {invoice.address}
                      </p>
                    </div>
                    <button
                      onClick={copyAddress}
                      style={{
                        width: 44, height: 44, borderRadius: 13, flexShrink: 0,
                        background: T.blue, color: "#fff",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        border: "none", cursor: "pointer",
                        transition: "transform 0.15s",
                      }}
                      onMouseEnter={e => e.currentTarget.style.transform = "scale(1.06)"}
                      onMouseLeave={e => e.currentTarget.style.transform = ""}
                    >
                      <FaCopy />
                    </button>
                  </div>

                  <button
                    onClick={handleVerifyPayment}
                    style={{
                      width: "100%", marginTop: 18, padding: "15px 0",
                      borderRadius: 16, background: T.blue,
                      color: "#fff", fontWeight: 900, fontSize: 11,
                      textTransform: "uppercase", letterSpacing: "0.18em",
                      border: "none", cursor: "pointer",
                      transition: "background 0.2s",
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = "#2563eb"}
                    onMouseLeave={e => e.currentTarget.style.background = T.blue}
                  >
                    Verify Payment
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* ── EXPIRED INVOICE ── */}
          <AnimatePresence>
            {invoice?.status === "expired" && (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0 }}
                style={{ margin: "clamp(16px, 4vw, 24px) clamp(16px, 4vw, 32px) 0", position: "relative", zIndex: 1 }}
              >
                <div style={{
                  background: T.bgCard2,
                  border: `1px solid rgba(239,68,68,0.3)`,
                  borderRadius: 28, padding: "clamp(20px, 4vw, 28px)", textAlign: "center",
                }}>
                  <div style={{
                    width: 64, height: 64, margin: "0 auto 16px",
                    borderRadius: "50%",
                    background: "rgba(239,68,68,0.1)",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: 26,
                  }}>
                    ⏰
                  </div>
                  <h3 style={{ fontSize: "1.2rem", fontWeight: 900, color: T.textPrimary, margin: "0 0 8px" }}>
                    Invoice Expired
                  </h3>
                  <p style={{ color: T.textMuted, fontSize: 13, margin: 0 }}>
                    This payment invoice has expired after 4 hours.
                  </p>
                  <button
                    onClick={() => setShowPlans(true)}
                    style={{
                      marginTop: 20, padding: "14px 32px",
                      borderRadius: 16, background: T.blue, color: "#fff",
                      fontWeight: 900, fontSize: 11, textTransform: "uppercase",
                      letterSpacing: "0.18em", border: "none", cursor: "pointer",
                      width: "100%", maxWidth: 250,
                    }}
                  >
                    Generate New Invoice
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* ── PROFILE ROW ── */}
          <div style={{
            position: "relative", zIndex: 1,
            padding: "clamp(24px, 5vw, 32px) clamp(16px, 5vw, 32px) 0",
            display: "flex", flexWrap: "wrap",
            justifyContent: "space-between", alignItems: "center",
            gap: 24,
          }}>
            <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 20 }}>
              <motion.div
                initial={{ scale: 0, rotate: -20 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{ type: "spring", stiffness: 240, damping: 16, delay: 0.15 }}
                style={{ position: "relative", flexShrink: 0 }}
              >
                <div style={{
                  width: "clamp(64px, 10vw, 76px)", height: "clamp(64px, 10vw, 76px)", borderRadius: 26,
                  background: `linear-gradient(135deg,${T.blue},#6366f1,#0ea5e9)`,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  boxShadow: "0 12px 32px rgba(59,130,246,0.3)",
                }}>
                  <FaUserCircle size={"clamp(32px, 5vw, 42px)"} color="#fff" />
                </div>
                <div style={{
                  position: "absolute", bottom: -2, right: -2,
                  width: 22, height: 22, border: `4px solid ${T.bgCard}`,
                  borderRadius: "50%",
                  background: stats.isActive ? T.success : T.textMuted,
                }} />
              </motion.div>

              <div style={{ minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 5 }}>
                  <FaShieldAlt size={10} color={T.blue} />
                  <span style={{ color: T.blue, fontWeight: 900, fontSize: 9, letterSpacing: "0.2em", textTransform: "uppercase" }}>
                    Verified Secure Node
                  </span>
                </div>
                <h1 style={{
                  fontSize: "clamp(1.4rem, 6vw, 2.6rem)",
                  fontWeight: 900, color: T.textPrimary,
                  letterSpacing: "-0.03em", margin: "0 0 10px",
                  whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: "100%",
                }}>
                  {stats.name}
                </h1>
                <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
                  <span style={{
                    padding: "5px 14px", borderRadius: 999,
                    background: T.blueDim, color: T.blue,
                    fontSize: 9, fontWeight: 900,
                    border: `1px solid rgba(59,130,246,0.3)`,
                    textTransform: "uppercase",
                  }}>
                    Tier: {activePlanDisplayName}
                  </span>
                  <span style={{ fontSize: 11, color: T.textMuted, fontWeight: 700 }}>
                    ID: {FetchUserData?._id?.slice(-8).toUpperCase()}
                  </span>
                </div>
              </div>
            </div>

            <div style={{ display: "flex", flexWrap: "wrap", gap: 14, width: "100%", flex: "1 1 auto", justifyContent: "flex-end" }}>
{(!stats.isActivePlan || latestPlanClosed) && (
                  <motion.button
                  whileHover={{ y: -3, boxShadow: "0 16px 36px rgba(234,179,8,0.35)" }}
                  whileTap={{ scale: 0.97 }}
                  onClick={() => setShowPlans(true)}
                  style={{
                    padding: "16px 36px", borderRadius: 16,
                    background: T.accent, color: "#0a0d14",
                    fontWeight: 900, fontSize: 11, textTransform: "uppercase",
                    letterSpacing: "0.16em", border: "none", cursor: "pointer",
                    display: "flex", alignItems: "center", justifyContent: "center", gap: 10,
                    boxShadow: "0 8px 24px rgba(234,179,8,0.25)",
                    flexGrow: window.innerWidth < 600 ? 1 : 0,
                  }}
                >
                  Start Investing <FaArrowRight size={11} />
                </motion.button>
              )}

              {withdrawHistory.find(wr => ["pending", "initiated", "processing"].includes(wr.status)) && (
                <motion.button
                  whileHover={{ scale: 1.04 }}
                  whileTap={{ scale: 0.96 }}
                  disabled={loading}
                  onClick={() => handleRefreshStatus(withdrawHistory.find(wr => ["pending", "initiated", "processing"].includes(wr.status))._id)}
                  style={{
                    padding: "13px 22px", borderRadius: 16,
                    background: T.blue, color: "#fff",
                    fontWeight: 900, fontSize: 11,
                    border: `1px solid #2563eb`,
                    cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                    boxShadow: "0 8px 20px rgba(59,130,246,0.25)",
                    flexGrow: window.innerWidth < 600 ? 1 : 0,
                  }}
                >
                  <FaHistory style={{ animation: loading ? "spin 1s linear infinite" : "none" }} />
                  {loading ? "Checking..." : "Check Withdraw Status"}
                </motion.button>
              )}
            </div>
          </div>

          {/* ── PLAN CAP PROGRESS ── */}
          <div style={{ position: "relative", zIndex: 1, padding: "clamp(20px, 4vw, 28px) clamp(16px, 4vw, 32px) clamp(24px, 5vw, 36px)" }}>
            <div style={{
              background: "rgba(0,0,0,0.25)",
              border: `1px solid ${T.border}`,
              borderRadius: 28, padding: "clamp(20px, 4vw, 28px)",
            }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: 16 }}>
                <h3 style={{ fontSize: 10, fontWeight: 900, color: T.textPrimary, textTransform: "uppercase", letterSpacing: "0.18em", margin: 0 }}>
                  Plan Cap Progress
                </h3>
                <span style={{ fontSize: "clamp(1.4rem, 4vw, 1.7rem)", fontWeight: 900, color: T.blue, fontStyle: "italic" }}>
                  {Math.round(planProgress.percent)}%
                </span>
              </div>

              <div style={{
                height: 18, width: "100%",
                background: "rgba(0,0,0,0.4)",
                borderRadius: 999,
                border: `1px solid ${T.border}`,
                padding: 3, overflow: "hidden",
              }}>
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: `${planProgress.percent}%` }}
                  transition={{ duration: 1.2, ease: "easeOut" }}
                  style={{
                    height: "100%", borderRadius: 999,
                    background: `linear-gradient(90deg,#60a5fa,${T.blue},#4f46e5)`,
                    boxShadow: "0 0 14px rgba(59,130,246,0.5)",
                  }}
                />
              </div>
            </div>
          </div>
        </motion.div>

        {/* ══════════ STAT CARDS ══════════ */}
        <div style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 220px), 1fr))",
          gap: 20,
        }}>
          <StatCard title="Total Earning" value={`$${Number(stats.totalEarnings).toFixed(2)}`} icon={FaWallet} accentColor={T.blue} delay={0} />
          <StatCard title="Total Points" value={stats.points} icon={FaStar} accentColor={T.accent} delay={0.08} />
          <StatCard title="Level" value={`Lvl ${stats.level}`} icon={FaCrown} accentColor="#a78bfa" delay={0.16} />
          <StatCard
            title={stats.isActivePlan ? "Plan Active" : "Plan Paused"}
            value={userPlan}
            icon={FaChartLine}
            accentColor={T.success}
            delay={0.24}
          />
        </div>

        {/* ══════════ ACTION GRID ══════════ */}
        <section style={{ display: "flex", flexDirection: "column", gap: 20 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
            <h2 style={{ fontSize: 10, fontWeight: 900, color: T.textMuted, textTransform: "uppercase", letterSpacing: "0.3em", margin: 0, whiteSpace: "nowrap" }}>
              System Controls
            </h2>
            <div style={{ height: 1, flex: 1, background: T.border }} />
          </div>
          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 140px), 1fr))",
            gap: 16,
          }}>
            <ActionButton icon={FaCoins} title="Assets" desc="View Plans" onClick={() => setShowPlans(true)} delay={0} />
            <ActionButton icon={FaUserFriends} title="Affiliate" desc="Your Team" onClick={() => setShowReferral(true)} delay={0.06} />
            <ActionButton icon={FaWallet} title="Payouts" desc="Withdraw" onClick={() => navigate("?tab=wallet")} delay={0.12} />
            <ActionButton icon={FaChartLine} title="Reports" desc="Stats Log" onClick={() => navigate("?tab=analytics")} delay={0.18} />
          </div>
        </section>
      </div>

      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

/* ── Stat Card ── */
function StatCard({ title, value, icon: Icon, accentColor, delay }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1], delay }}
      whileHover={{ y: -6 }}
      style={{
        padding: "clamp(20px, 4vw, 26px)", borderRadius: 26,
        background: "#0a1628",
        border: `1px solid ${T.border}`,
        boxShadow: "0 8px 32px rgba(0,0,0,0.35)",
        transition: "border-color 0.25s, box-shadow 0.25s",
        cursor: "default",
      }}
      onMouseEnter={e => {
        e.currentTarget.style.borderColor = `${accentColor}55`;
        e.currentTarget.style.boxShadow = `0 16px 40px rgba(0,0,0,0.4), 0 0 30px ${accentColor}22`;
      }}
      onMouseLeave={e => {
        e.currentTarget.style.borderColor = T.border;
        e.currentTarget.style.boxShadow = "0 8px 32px rgba(0,0,0,0.35)";
      }}
    >
      <div style={{
        width: 48, height: 48, borderRadius: 16,
        background: `${accentColor}18`,
        border: `1px solid ${accentColor}33`,
        display: "flex", alignItems: "center", justifyContent: "center",
        marginBottom: 20, color: accentColor,
      }}>
        <Icon size={19} />
      </div>
      <p style={{ fontSize: 9, fontWeight: 900, color: T.textMuted, textTransform: "uppercase", letterSpacing: "0.18em", margin: "0 0 8px" }}>
        {title}
      </p>
      <h4 style={{ fontSize: "clamp(1.3rem, 4vw, 1.5rem)", fontWeight: 900, color: T.textPrimary, margin: 0, letterSpacing: "-0.02em" }}>
        {value}
      </h4>
    </motion.div>
  );
}

/* ── Action Button ── */
function ActionButton({ icon: Icon, title, desc, onClick, delay }) {
  return (
    <motion.button
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1], delay }}
      whileHover={{ y: -5 }}
      whileTap={{ scale: 0.97 }}
      onClick={onClick}
      style={{
        textAlign: "left", padding: "clamp(16px, 3vw, 22px)", borderRadius: 22,
        background: "#0a1628",
        border: `1px solid ${T.border}`,
        cursor: "pointer",
        transition: "border-color 0.25s, box-shadow 0.25s",
      }}
      onMouseEnter={e => {
        e.currentTarget.style.borderColor = "rgba(234,179,8,0.4)";
        e.currentTarget.style.boxShadow = "0 16px 36px rgba(234,179,8,0.12)";
      }}
      onMouseLeave={e => {
        e.currentTarget.style.borderColor = T.border;
        e.currentTarget.style.boxShadow = "none";
      }}
    >
      <div style={{
        width: 52, height: 52, borderRadius: 16,
        background: "rgba(255,255,255,0.04)",
        border: `1px solid ${T.border}`,
        display: "flex", alignItems: "center", justifyContent: "center",
        marginBottom: 18, color: T.textSecondary,
        transition: "color 0.2s, border-color 0.2s",
      }}>
        <Icon size={20} />
      </div>
      <h3 style={{ fontWeight: 900, color: T.textPrimary, letterSpacing: "0.02em", textTransform: "uppercase", fontSize: 12, margin: "0 0 4px" }}>
        {title}
      </h3>
      <p style={{ fontSize: 9, color: T.textMuted, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.08em", margin: 0 }}>
        {desc}
      </p>
    </motion.button>
  );
}